import React, {useRef, useState, useCallback} from 'react';
import {Avatar, Button, Icon, DropdownMenu, Text} from '@gravity-ui/uikit';
import styles from './AvatarUpload.module.scss';
import {ArrowUpFromLine, Pencil, TrashBin} from '@gravity-ui/icons';
import {useToaster} from '@gravity-ui/uikit';
import {ActionModal} from '../../../../shared';
import {AvatarUploadModal} from '../AvatarUploadModal';
import {EntityType, PreparedAvatarData} from '../../lib';

interface AvatarUploadProps {
  src?: string;
  text?: string;
  onDelete?: () => void;
  isDeleting?: boolean;
  onUploadSuccess?: () => void;
  entityId?: string;
  entityType?: EntityType;
  isDeletedAccount?: boolean;
  isCreating?: boolean;
  onPrepareAvatarData?: (data: PreparedAvatarData) => void;
}

export const AvatarUpload = ({
  src,
  text,
  onDelete,
  isDeleting,
  onUploadSuccess,
  entityId,
  entityType = EntityType.USER,
  isDeletedAccount,
  isCreating = false,
  onPrepareAvatarData,
}: AvatarUploadProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {add} = useToaster();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [croppedPreviewUrl, setCroppedPreviewUrl] = useState<string | null>(null);

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      if (!file.type.startsWith('image/')) {
        add({
          name: 'avatar-upload-error',
          title: 'Ошибка загрузки аватара',
          content: 'Пожалуйста, выберите файл изображения.',
          theme: 'danger',
        });
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        const previewUrl = reader.result as string;
        setSelectedFile(file);
        setPreviewUrl(previewUrl);
        setIsUploadModalOpen(true);
      };
      reader.readAsDataURL(file);
      event.target.value = '';
    }
  };

  const handleOpenDeleteModal = () => {
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (onDelete) {
      onDelete();
    }
    setIsDeleteModalOpen(false);
  };

  const handleUploadSuccess = useCallback(() => {
    setIsUploadModalOpen(false);
    setSelectedFile(null);
    setPreviewUrl(null);
    if (onUploadSuccess) {
      onUploadSuccess();
    }
  }, [onUploadSuccess]);

  // Обработчик получения данных аватара и превью
  const handlePrepareAvatarDataInternal = useCallback((data: PreparedAvatarData, preview: string) => {
    if (onPrepareAvatarData) {
      onPrepareAvatarData(data);
    }
    // Обновляем превью аватара с обрезанным изображением
    setCroppedPreviewUrl(preview);
  }, [onPrepareAvatarData]);

  // Отображаем предпросмотр кропнутого изображения или исходное изображение
  const displaySrc = croppedPreviewUrl || src;

  return (
    <>
      <div className={styles.container}>
        <Avatar imgUrl={displaySrc || ''} text={text || ''} className={styles.avatar} size={'xl'} />
        <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" style={{display: 'none'}} />
        {src
          ? !isDeletedAccount && (
              <DropdownMenu
                renderSwitcher={(props) => (
                  <Button {...props} view="raised" size="m" className={styles.button}>
                    <Icon data={Pencil} />
                  </Button>
                )}
                size={'xl'}
                items={[
                  {
                    iconStart: <Icon data={ArrowUpFromLine} />,
                    action: handleButtonClick,
                    text: 'Загрузить',
                  },
                  {
                    iconStart: <Icon data={TrashBin} />,
                    action: handleOpenDeleteModal,
                    text: 'Удалить',
                    className: styles.delete,
                  },
                ]}
              />
            )
          : !isDeletedAccount && (
              <Button view="raised" size="m" onClick={handleButtonClick} className={styles.button}>
                <Icon data={ArrowUpFromLine} />
              </Button>
            )}
      </div>

      {onDelete && (
        <ActionModal
          open={isDeleteModalOpen}
          setOpen={setIsDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          submitForm={handleConfirmDelete}
          buttonsSize="l"
          size="s"
          caption="Удаление аватара"
          textButtonApply="Удалить"
          textButtonCancel="Отмена"
          viewActionButton="outlined-danger"
          isLoading={isDeleting}>
          <Text variant={'body-2'} color={'secondary'}>
            Вы уверены, что хотите удалить аватар?
          </Text>
        </ActionModal>
      )}

      <AvatarUploadModal
        open={isUploadModalOpen}
        setOpen={setIsUploadModalOpen}
        file={selectedFile}
        previewUrl={previewUrl}
        onUploadSuccess={handleUploadSuccess}
        entityId={entityId}
        entityType={entityType}
        isCreating={isCreating}
        onPrepareAvatarData={handlePrepareAvatarDataInternal}
      />
    </>
  );
};
